import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Plus, Trash2, ToggleLeft, ToggleRight, Tag, Percent, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCoupons = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Create form
  const [form, setForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_order: 0,
    max_uses: 1,
    description: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/coupons`);
      const data = await res.json();
      setCoupons(data.coupons || []);
    } catch {
      toast.error('Errore caricamento coupon');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.code.trim()) {
      toast.error(language === 'it' ? 'Inserisci un codice' : 'Enter a code');
      return;
    }
    if (form.discount_value <= 0) {
      toast.error(language === 'it' ? 'Valore sconto non valido' : 'Invalid discount value');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/coupons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, code: form.code.trim().toUpperCase(), active: true })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(language === 'it' ? 'Coupon creato!' : 'Coupon created!');
        setForm({ code: '', discount_type: 'percentage', discount_value: 10, min_order: 0, max_uses: 1, description: '' });
        setShowCreate(false);
        fetchCoupons();
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch {
      toast.error('Errore creazione coupon');
    }
  };

  const toggleActive = async (code, currentActive) => {
    try {
      await fetch(`${API_URL}/api/coupons/${code}?active=${!currentActive}`, { method: 'PATCH' });
      toast.success(currentActive ? 'Coupon disattivato' : 'Coupon attivato');
      fetchCoupons();
    } catch {
      toast.error('Errore');
    }
  };

  const deleteCoupon = async (code) => {
    try {
      await fetch(`${API_URL}/api/coupons/${code}`, { method: 'DELETE' });
      toast.success('Coupon eliminato');
      setDeleteConfirm(null);
      fetchCoupons();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <div className="max-w-5xl mx-auto p-6 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <button onClick={() => navigate('/admin/inventory')} className="flex items-center gap-2 text-sm hover:text-[var(--brand-primary)] transition-colors" style={{ color: 'var(--text-muted)' }}>
              <ArrowLeft size={18} /> Inventario
            </button>
            <button onClick={() => navigate('/admin/orders')} className="btn-secondary text-sm py-2 px-4">Ordini</button>
            <button onClick={() => navigate('/admin/newsletter')} className="btn-secondary text-sm py-2 px-4">Newsletter</button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="coupons-title">
                <Tag className="inline mr-3 mb-1" size={32} />
                {language === 'it' ? 'Gestione Coupon' : 'Coupon Management'}
              </h1>
              <p style={{ color: 'var(--text-muted)' }}>
                {language === 'it' ? 'Crea e gestisci codici sconto per i clienti' : 'Create and manage discount codes'}
              </p>
            </div>
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="btn-primary flex items-center gap-2"
              data-testid="create-coupon-btn"
            >
              <Plus size={18} />
              {language === 'it' ? 'Nuovo Coupon' : 'New Coupon'}
            </button>
          </div>
        </div>

        {/* Create Form */}
        {showCreate && (
          <div className="dark-card p-6 mb-8" data-testid="create-coupon-form">
            <h2 className="text-xl font-bold mb-4">
              {language === 'it' ? 'Crea Nuovo Coupon' : 'Create New Coupon'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'it' ? 'Codice' : 'Code'} *
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="ES: SCONTO10"
                  className="w-full rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] uppercase"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                  data-testid="coupon-code-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'it' ? 'Tipo Sconto' : 'Discount Type'}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, discount_type: 'percentage' }))}
                    className={`flex-1 p-3 rounded text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                      form.discount_type === 'percentage' ? 'ring-2 ring-[var(--brand-primary)] text-[var(--brand-primary)]' : ''
                    }`}
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}
                    data-testid="type-percentage-btn"
                  >
                    <Percent size={16} /> Percentuale
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, discount_type: 'fixed' }))}
                    className={`flex-1 p-3 rounded text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                      form.discount_type === 'fixed' ? 'ring-2 ring-[var(--brand-primary)] text-[var(--brand-primary)]' : ''
                    }`}
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}
                    data-testid="type-fixed-btn"
                  >
                    <DollarSign size={16} /> Fisso (€)
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'it' ? 'Valore Sconto' : 'Discount Value'} *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={form.discount_value}
                    onChange={(e) => setForm(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    step="1"
                    className="w-full rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                    data-testid="discount-value-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-muted)' }}>
                    {form.discount_type === 'percentage' ? '%' : '€'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'it' ? 'Utilizzi Massimi' : 'Max Uses'}
                  <span className="font-normal ml-1" style={{ color: 'var(--text-muted)' }}>(0 = illimitato)</span>
                </label>
                <input
                  type="number"
                  value={form.max_uses}
                  onChange={(e) => setForm(prev => ({ ...prev, max_uses: parseInt(e.target.value) || 0 }))}
                  min="0"
                  className="w-full rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                  data-testid="max-uses-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'it' ? 'Ordine Minimo (€)' : 'Min Order (€)'}
                </label>
                <input
                  type="number"
                  value={form.min_order}
                  onChange={(e) => setForm(prev => ({ ...prev, min_order: parseFloat(e.target.value) || 0 }))}
                  min="0"
                  step="1"
                  className="w-full rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                  data-testid="min-order-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'it' ? 'Descrizione (opzionale)' : 'Description (optional)'}
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="es: Sconto benvenuto"
                  className="w-full rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                  data-testid="description-input"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleCreate} className="btn-primary px-6" data-testid="save-coupon-btn">
                {language === 'it' ? 'Crea Coupon' : 'Create Coupon'}
              </button>
              <button onClick={() => setShowCreate(false)} className="btn-secondary px-6">
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* Coupons List */}
        <div className="dark-card p-6">
          <h2 className="text-xl font-bold mb-4">
            {language === 'it' ? `Coupon Attivi (${coupons.length})` : `Active Coupons (${coupons.length})`}
          </h2>

          {loading ? (
            <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Caricamento...</p>
          ) : coupons.length === 0 ? (
            <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
              {language === 'it' ? 'Nessun coupon ancora. Creane uno!' : 'No coupons yet. Create one!'}
            </p>
          ) : (
            <div className="space-y-3">
              {coupons.map((c) => (
                <div
                  key={c.code}
                  className="flex items-center justify-between p-4 rounded transition-all"
                  style={{
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-secondary)',
                    opacity: c.active ? 1 : 0.5
                  }}
                  data-testid={`coupon-row-${c.code}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Toggle */}
                    <button
                      onClick={() => toggleActive(c.code, c.active)}
                      className="transition-colors"
                      style={{ color: c.active ? '#22c55e' : 'var(--text-muted)' }}
                      data-testid={`toggle-${c.code}`}
                    >
                      {c.active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </button>

                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-[var(--brand-primary)]">{c.code}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          c.active ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'
                        }`}>
                          {c.active ? 'Attivo' : 'Disattivato'}
                        </span>
                      </div>
                      <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                        {c.description || (c.discount_type === 'percentage' ? `${c.discount_value}% di sconto` : `€${c.discount_value} di sconto`)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Discount badge */}
                    <div className="text-center">
                      <span className="text-2xl font-bold text-[var(--brand-primary)]">
                        {c.discount_type === 'percentage' ? `${c.discount_value}%` : `€${c.discount_value}`}
                      </span>
                    </div>

                    {/* Usage */}
                    <div className="text-center">
                      <p className="text-lg font-bold">{c.times_used}/{c.max_uses === 0 ? '∞' : c.max_uses}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {language === 'it' ? 'Utilizzi' : 'Uses'}
                      </p>
                    </div>

                    {/* Min order */}
                    {c.min_order > 0 && (
                      <div className="text-center">
                        <p className="text-sm font-semibold">€{c.min_order}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Min</p>
                      </div>
                    )}

                    {/* Delete */}
                    <button
                      onClick={() => setDeleteConfirm(c.code)}
                      className="text-red-400 hover:text-red-300 transition-colors p-2"
                      data-testid={`delete-${c.code}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
            <div className="p-6 rounded-lg max-w-sm w-full" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }} onClick={e => e.stopPropagation()}>
              <h3 className="font-bold text-lg mb-2">{language === 'it' ? 'Conferma Eliminazione' : 'Confirm Delete'}</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {language === 'it' ? `Eliminare il coupon ${deleteConfirm}?` : `Delete coupon ${deleteConfirm}?`}
              </p>
              <div className="flex gap-3">
                <button onClick={() => deleteCoupon(deleteConfirm)} className="btn-primary px-4 py-2 text-sm" style={{ background: '#ef4444' }}>
                  {language === 'it' ? 'Elimina' : 'Delete'}
                </button>
                <button onClick={() => setDeleteConfirm(null)} className="btn-secondary px-4 py-2 text-sm">
                  {language === 'it' ? 'Annulla' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoupons;
